import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useGenerateFormMutation, useSaveFormMutation, useDownloadFormMutation } from "./formApiSlice"
import { showSpinner, hideSpinner } from "../../components/componentSlice"
import FormRenderer from "./FormRenderer"
import SearchableDropdown from "../../components/SearchableDropdown"
import { Modal } from "antd"
import { downloadFile } from "../utils/Utilities"
import { selectCrm } from "../crm/crmSlice"
import { useGetFieldOptionsByCrmMutation } from "../crm/fieldOptionApiSlice"

import "./FormViewer.css"

const FormViewer = ({ bundle, clientId, accountId, openModal, handleModalOpen }) => {

    const dispatch = useDispatch()

    const crm = useSelector(selectCrm)

    const [selectedFormType, setSelectedFormType] = useState(null)
    const [activeForm, setActiveForm] = useState(null)
    const [errorMessage, setErrorMessage] = useState(null)
    const [fieldOptions, setFieldOptions] = useState(null)

    const [generateForm, {isLoading: isGenerateFormLoading}] = useGenerateFormMutation()
    const [downloadForm, {isLoading: isDownloadFormLoading}] = useDownloadFormMutation()
    const [saveForm, {isLoading: isSaveFormLoading}] = useSaveFormMutation()
    const [getFieldOptionsByCrm,  { isLoading: isGetFieldOptionsLoading }] = useGetFieldOptionsByCrmMutation()

    useEffect(() => {
        if(isGenerateFormLoading || isSaveFormLoading || isDownloadFormLoading || isGetFieldOptionsLoading) {
            dispatch(showSpinner())
        } else{
            dispatch(hideSpinner())
        }
    }, [isGenerateFormLoading, isSaveFormLoading, isDownloadFormLoading, isGetFieldOptionsLoading])

    useEffect(() => {
        if(bundle?.forms?.length > 0) {
            handleSelectFormTypeChange(bundle.forms[0])
        }

    }, [bundle])

    useEffect(() => {
        async function handleGetFieldOptions() {
            try {
                const response = await getFieldOptionsByCrm(crm).unwrap()
                if(response?.fieldOptions) {
                    setFieldOptions(response.fieldOptions)
                }
            } catch(err) {
                setErrorMessage(err.data?.detail)
            }
        }
        if(crm) {
            handleGetFieldOptions()
        }
    }, [crm])
    

    const handleGenerateForm = async (form) => {
        setErrorMessage("")

        if(!clientId || !accountId) {
            return
        }

        try {
            let response = await generateForm({
                clientId: clientId,
                accountId: accountId,
                formType: form.id
            }).unwrap()

            if(response) {
                setActiveForm(response)
            }
        } catch(err) {
            if(!err.status || err.status === "FETCH_ERROR") {
                setErrorMessage("No Server Response")
            } else {
                setErrorMessage(err.data?.detail)
            }
        }
    }

    const handleSaveForm = async (fields) => {
        try {
            let request = {
                clientId: clientId,
                accountId: accountId,
                fields: fields
            }

            await saveForm(request).unwrap()
        } catch(err) {
            if(!err.status || err.status === "FETCH_ERROR") {
                setErrorMessage("No Server Response")
            } else {
                setErrorMessage(err.data?.detail)
            }
        }
    }

    const handleDownloadForm = async () => {
        if(!clientId || !accountId) {
            return
        }

        try {
            const data = await downloadForm({
                clientId: clientId,
                accountId: accountId,
                formTypes: bundle.forms.map(form => form.id)
            }).unwrap()
            console.log(data)

            downloadFile(data, "forms.zip")
        } catch(err) {
            if(err.status === "FETCH_ERROR") {
                setErrorMessage("No Server Response");
            } else if(err.status === 400) {
                setErrorMessage("Error exporting file");
            } else if(err.status === 401) {
                setErrorMessage("Unauthorized");
            } else {
                setErrorMessage(err.data?.message);
            }
        }
    }

    const getFormOptions = () => {
        if(!bundle) {
            return
        }

        return bundle.forms.map(form => ({id: form, name: form.name}))
    }

    const handleSelectFormTypeChange = (form) => {
        setSelectedFormType(form)
        handleGenerateForm(form)
    }

    return (
        <Modal className="form-viewer-modal" width="100%" open={openModal} onCancel={() => handleModalOpen(false)} footer={[]}>
            <div>
                <div className='top-header'>
                    <header>Form Viewer</header>
                </div>
                {errorMessage && 
                    <div className="support-error">
                        <p>{errorMessage}</p>
                    </div>
                }
                <div className="form-viewer-type-selector">
                    <div><h3 className="header">Form: </h3></div>
                    <div className="search-dropdown">
                        <SearchableDropdown
                            options={getFormOptions()}
                            label="name"
                            id="id"
                            selectedVal={selectedFormType?.name}
                            placeholderVal={"Select Form..."}
                            handleChange={(val, id) => handleSelectFormTypeChange(id)}
                        />
                    </div>
                </div>
                <div className="form-viewer">
                    <div className="form-viewer-header">
                        <header>{selectedFormType?.name || "No selected form..."}</header>
                    </div>
                    {activeForm?.form && <FormRenderer 
                                            pdfData={activeForm.form} 
                                            formName={selectedFormType?.name} 
                                            formFieldValues={activeForm.fields}
                                            requiredFields={activeForm.requiredFields}
                                            handleSaveForm={handleSaveForm}
                                            handleDownloadForm={handleDownloadForm}
                                            fieldOptions={fieldOptions}
                                        />
                    }
                </div>
            </div>
        </Modal>
    )
}

export default FormViewer